import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/NewsSection.css';

const NewsSection = () => {
  const [newsData, setNewsData] = useState({
    featured: null,
    latest: [],
    categories: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API configuration
  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  // Fetch news data from backend
  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('📰 Fetching news data for NewsSection...');
        
        // Fetch featured news and latest news
        const [featuredResponse, latestResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/news/featured?limit=1`),
          fetch(`${API_BASE_URL}/api/news?limit=5`)
        ]);

        if (!featuredResponse.ok || !latestResponse.ok) {
          throw new Error(`API error: ${featuredResponse.status} or ${latestResponse.status}`);
        }

        const featuredData = await featuredResponse.json();
        const latestData = await latestResponse.json();

        console.log('✅ NewsSection data fetched:', { featuredData, latestData });

        setNewsData({
          featured: featuredData.data?.[0] || null,
          latest: latestData.data?.slice(0, 3) || [],
          categories: latestData.data?.slice(0, 2) || []
        });
        
      } catch (err) {
        console.error('❌ Error fetching news for NewsSection:', err);
        setError(err.message);
        
        // Use fallback data
        setNewsData(getFallbackNewsData());
      } finally {
        setLoading(false);
      }
    };

    fetchNewsData();
  }, [API_BASE_URL]);

  // Fallback data when API fails
  const getFallbackNewsData = () => ({
    featured: {
      id: 1,
      title: 'Công ty PhucAn Solutions đạt giải thưởng "Doanh nghiệp tiêu biểu 2024"',
      excerpt: 'Với những thành tích xuất sắc trong lĩnh vực công nghệ IoT, năng lượng sạch và kiến trúc xanh, PhucAn Solutions vinh dự nhận giải thưởng danh giá từ Hiệp hội Doanh nghiệp TP.HCM...',
      image: '',
      category: 'Tin nổi bật',
      createdAt: '2024-12-18',
      slug: 'giai-thuong-doanh-nghiep-tieu-bieu-2024'
    },
    latest: [
      {
        id: 2,
        title: 'Xu hướng IoT trong smart home 2024',
        image: '/images/iot-news-thumb.jpg',
        createdAt: '2024-12-15',
        category: 'IoT',
        slug: 'xu-huong-iot-smart-home-2024'
      },
      {
        id: 3,
        title: 'Lợi ích của năng lượng mặt trời cho gia đình',
        image: '',
        createdAt: '2024-12-12',
        category: 'Solar',
        slug: 'loi-ich-nang-luong-mat-troi'
      },
      {
        id: 4,
        title: 'Thiết kế sân vườn nhỏ cho căn hộ chung cư',
        image: '',
        createdAt: '2024-12-10',
        category: 'Garden',
        slug: 'thiet-ke-san-vuon-nho'
      }
    ],
    categories: [
      {
        id: 5,
        title: 'Hệ thống Smart Home thông minh tại Phú Mỹ Hưng',
        image: '',
        category: 'IoT & CCTV',
        createdAt: '2024-12-16',
        slug: 'smart-home-phu-my-hung'
      },
      {
        id: 6,
        title: 'Xu hướng kiến trúc xanh và bền vững',
        image: '',
        category: 'Kiến trúc',
        createdAt: '2024-12-14',
        slug: 'xu-huong-kien-truc-xanh'
      }
    ]
  });

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get image URL
  const getImageUrl = (image) => {
    if (!image || image.startsWith('/images/')) {
      return image || '/images/placeholder-news.svg';
    }
    
    // If image starts with /uploads, add API base URL
    if (image.startsWith('/uploads/')) {
      return `${API_BASE_URL}${image}`;
    }
    
    return image;
  };

  // Create slug from title if not available
  const createSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[đĐ]/g, 'd')
      .replace(/[^a-z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  };

  // Loading component
  const LoadingCard = ({ type = 'default' }) => (
    <div className={`loading-card ${type}`}>
      <div className="loading-shimmer"></div>
    </div>
  );

  if (loading) {
    return (
      <section className="news-section" id="news">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">TIN TỨC & SỰ KIỆN</h2>
            <p className="section-subtitle">Đang tải tin tức...</p>
          </div>
          <div className="news-layout">
            <div className="news-main">
              <LoadingCard type="main" />
            </div>
            <div className="news-sidebar">
              <LoadingCard type="sidebar" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="news-section" id="news">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">TIN TỨC & SỰ KIỆN</h2>
          <p className="section-subtitle">
            {error ? '⚠️ Đang hiển thị dữ liệu mẫu' : 'Cập nhật những tin tức mới nhất về công nghệ và dự án'}
          </p>
        </div>
        
        <div className="news-layout">
          {/* Main Featured Article */}
          <div className="news-main">
            {newsData.featured ? (
              <div className="main-article">
                <div className="main-article-image">
                  <img 
                    src={getImageUrl(newsData.featured.image)} 
                    alt={newsData.featured.title}
                    onError={(e) => {
                      e.target.src = '/images/featured-news-main.jpg';
                    }}
                  />
                  <div className="article-overlay">
                    <div className="article-meta">
                      <span className="category-tag">{newsData.featured.category || 'Tin nổi bật'}</span>
                      <span className="article-date">{formatDate(newsData.featured.createdAt)}</span>
                    </div>
                    <h3>{newsData.featured.title}</h3>
                    <p>{newsData.featured.excerpt || newsData.featured.description || ''}</p>
                    <Link 
                      to={`/news/${newsData.featured.slug || createSlug(newsData.featured.title)}`} 
                      className="read-more-btn"
                    >
                      Đọc tiếp →
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-featured">Chưa có tin nổi bật</div>
            )}
            
            {/* Bottom Articles */}
            <div className="bottom-articles">
              {newsData.categories.map(article => (
                <div className="bottom-article" key={article.id}>
                  <img 
                    src={getImageUrl(article.image)} 
                    alt={article.title}
                    onError={(e) => {
                      e.target.src = '/images/placeholder-news.svg';
                    }}
                  />
                  <div className="bottom-article-content">
                    <span className="bottom-category">{article.category}</span>
                    <h4>{article.title}</h4>
                    <span className="bottom-date">{formatDate(article.createdAt)}</span>
                  </div>
                </div>
              ))}
              {newsData.categories.length === 0 && (
                <div className="no-content">Chưa có bài viết nào</div>
              )}
            </div>
          </div>
          
          {/* Side News */}
          <div className="news-sidebar">
            <div className="sidebar-header">
              <h3>Tin mới nhất</h3>
            </div>
            {newsData.latest.map(item => (
              <Link 
                to={`/news/${item.slug || createSlug(item.title)}`}
                className="sidebar-news-item" 
                key={item.id}
              >
                <div className="sidebar-image">
                  <img 
                    src={getImageUrl(item.image)} 
                    alt={item.title}
                    onError={(e) => {
                      e.target.src = '/images/placeholder-news.svg';
                    }}
                  />
                  <span className="sidebar-category">{item.category}</span>
                </div>
                <div className="sidebar-content">
                  <h4>{item.title}</h4>
                  <span className="news-date">{formatDate(item.createdAt)}</span>
                </div>
              </Link>
            ))}
            
            {newsData.latest.length === 0 && (
              <div className="no-content">Chưa có tin tức mới</div>
            )}
            
            <div className="view-all-news">
              <Link to="/news" className="view-all-btn">
                Xem tất cả tin tức →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsSection; 